import * as React from "react";
import { AlertCircle } from "lucide-react";

import { cn } from "../../utils/cn";
import { Label } from "./label";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, hint, required, id, ...props }, ref) => {
    const generatedId = React.useId();
    const textareaId = id || generatedId;

    const textareaElement = (
      <textarea
        id={textareaId}
        className={cn(
          "flex min-h-[80px] w-full rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 text-sm font-body text-surface-on shadow-ambient-sm ring-offset-background placeholder:text-outline/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-150 dark:bg-[#0D0E10] dark:border-[#434939] dark:text-white dark:placeholder:text-outline",
          error && "border-error focus-visible:ring-error",
          className,
        )}
        ref={ref}
        required={required}
        {...props}
      />
    );

    if (!label && !error && !hint) {
      return textareaElement;
    }

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {label && (
          <Label
            htmlFor={textareaId}
            required={required}
            className="text-xs font-bold uppercase tracking-widest text-on-surface-variant"
          >
            {label}
          </Label>
        )}
        {textareaElement}
        {error && (
          <p className="flex items-center gap-1.5 text-xs text-error font-label font-bold animate-fade-in">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {error}
          </p>
        )}
        {hint && !error && (
          <p className="text-xs text-surface-on-variant dark:text-[#C3C9B4] font-body mt-1">
            {hint}
          </p>
        )}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
